/**
 * Represents an inclusive calendar week range.
 */
export interface WeekRange {
    start: Date;
    end: Date;
}

/**
 * Returns the Sunday–Saturday week range that contains the given date.
 * Defaults to today if no date is provided.
 *
 * @param from - The source date used to calculate the week range.
 * @returns The inclusive week range for the supplied date.
 */
export function getCurrentWeekRange(from: Date = new Date()): WeekRange {
    const date = new Date(from);
    const dayOfWeek = date.getDay();

    const start = new Date(date);
    start.setDate(date.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
}

/**
 * Returns true when the supplied date falls within the current Sunday–Saturday week.
 *
 * @param date - The date string or Date instance to test.
 * @returns True when the value is within the current week.
 */
export function isWithinCurrentWeek(date: string | Date): boolean {
    const { start, end } = getCurrentWeekRange();
    const target = new Date(date);

    return target >= start && target <= end;
}
